import { NA_COLOR, THEME } from "../constants";
import {
  selectAttributesIncludingExperimentsAndModalities,
  selectContractFieldBookColors,
} from "../selectors/resultMap";
import { useCallback, useMemo } from "react";

import chroma from "chroma-js";
import { useSelector } from "react-redux";

// Status colors, deliberately not brand tokens: the green matching $green is a
// coincidence of hex, not a shared role. Kept local since they have no SCSS twin.
const YES_COLOR = "#8fbf48";
const NO_COLOR = "#dd444c";

// Canonical, deterministic value order for an attribute: unique + sorted, so a
// given value always maps to the same palette slot in every consumer.
const canonicalValues = (values) =>
  [...new Set((values ?? []).filter((v) => v != null))].sort();

// Colors for `count` distinct values. Up to the palette length we return the
// curated Tableau-10 entries as-is (first N are maximally distinct). Past it we
// interpolate the same palette across `count` points — one path, no overflow
// special-casing. ponytail: crossing the palette length re-colors the set, but
// >palette.length distinct values on one attribute is rare and overrides pin
// the ones that matter.
const paletteColors = (count) => {
  const palette = THEME.categoricalPalette;
  if (count <= palette.length) return palette;
  return chroma.scale(palette).mode("lab").colors(count);
};

// Generic hook for getting colors by attribute/category (modality, experiment, attributes, etc.).
// `siteColors` replaces the site's saved palette, for the editor which must
// preview its pending state (including a value it just reset) instead of the
// colours still stored on the trial.
export const useAttributeColor = (attribute, siteColors) => {
  const trial = useSelector(({ resultMap }) => resultMap.trial);
  const contractColors = useSelector(selectContractFieldBookColors);
  const attributeValues = useSelector(
    selectAttributesIncludingExperimentsAndModalities
  );

  const values = useMemo(
    () => canonicalValues(attributeValues[attribute]),
    [attributeValues, attribute]
  );
  const colors = useMemo(() => paletteColors(values.length), [values.length]);

  return useCallback(
    (value) => {
      if (value == null || value === "") return NA_COLOR;

      // Site override wins over everything else, then the company palette
      // inherited from the contract. Both beat the yes/no convention below:
      // they are explicit client choices.
      const override = (siteColors ?? trial?.preferences?.fieldBookColors)?.[
        attribute
      ]?.[value];
      if (override) return override;

      const inherited = contractColors?.[attribute]?.[value];
      if (inherited) return inherited;

      // Semantic yes/no keep their conventional green/red.
      const lower = value.toString().toLowerCase();
      if (["yes", "true"].includes(lower)) return YES_COLOR;
      if (["no", "false"].includes(lower)) return NO_COLOR;

      const index = values.indexOf(value);
      // ponytail: value absent from the trial's distinct set (rare, e.g. colored
      // outside the resultMap context) → stable hash fallback so nothing crashes.
      if (index === -1) return THEME.modalityColorHash.hex(value);

      return colors[index];
    },
    [trial, siteColors, contractColors, attribute, values, colors]
  );
};

export const useModalityBadgeStyle = () => {
  const getModalityColor = useAttributeColor("modality");
  return useCallback(
    (modality) => {
      const color = getModalityColor(modality);
      return {
        border: `1px solid ${color}`,
        background: chroma(color).brighten(2.5).hex(),
        color,
        fontWeight: "700",
        borderRadius: "6px",
        padding: "3px",
      };
    },
    [getModalityColor]
  );
};
